import {
  NotificationPreferencesInput,
  NotificationPreferencesSchema,
  ProfileBioInput,
  ProfileImageInput,
  ProfileImageInputSchema,
  ProposalPreferencesSchema,
  UserDetailsFormSchema,
  UserDetailsInputs,
  useAuthenticatedUser,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  EmptyState,
  FlexBox,
  Loader,
  LoadingButton,
  PhoneWithOptInInput,
  SMSPhoneOptInSchema,
} from '@airship-platform/jam-web/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { z } from 'zod';

const ProfileFormSchema = z
  .object({})
  .merge(ProfileImageInputSchema)
  .merge(UserDetailsFormSchema)
  .merge(ProposalPreferencesSchema)
  .merge(NotificationPreferencesSchema)
  .merge(SMSPhoneOptInSchema);

type ProfileFormInputs = z.infer<typeof ProfileFormSchema>;

const copyDict: Record<string, string> = {
  newUser: "Let's make sure your personal information is correct",
  existingUser: 'Your profile',
};

const ProfilePage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [queryParam] = useSearchParams();
  const userTypeParam = queryParam.get('userType') || 'existingUser';
  const { showToast } = useToast();
  const {
    user,
    fetchUser,
    updateUser,
    uploadAvatar,
    isLoading: userIsLoading,
    isLoaded: userIsLoaded,
  } = useAuthenticatedUser();

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const profileForm = useForm<ProfileFormInputs>({
    values: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      title: user?.title || '',
      proposalPreferences: {
        quote: user?.proposalPreferences?.quote || '',
      },
      phone: user?.phone || '',
      smsOptInConsent: user?.smsOptInConsent?.consented || false,
      notificationPreferences: {
        appointmentReminder:
          user?.notificationPreferences?.appointmentReminder ?? true,
        viewProposalNotification:
          user?.notificationPreferences?.viewProposalNotification ?? true,
        acceptProposalNotification:
          user?.notificationPreferences?.acceptProposalNotification ?? true,
      },
      profileImage: user?.profileImage || undefined,
    },
    mode: 'onSubmit',
    resolver: zodResolver(ProfileFormSchema),
  });

  const { handleSubmit } = profileForm;

  if (userIsLoading && !userIsLoaded) {
    return <Loader isLoading />;
  }

  if (!user) {
    return userIsLoaded ? (
      <EmptyState
        title="User not found"
        subtitle="Not able to manage profile, contact support"
      />
    ) : null;
  }

  const handleSave = async ({ profileImage, ...data }: ProfileFormInputs) => {
    try {
      setIsLoading(true);

      if (profileImage instanceof File) {
        await uploadAvatar(user.userId, profileImage);
      }

      const { smsOptInConsent, ...rest } = data;

      await updateUser({
        ...rest,
        proposalPreferences: {
          ...rest.proposalPreferences,
          quote: rest.proposalPreferences.quote || '',
        },
        ...(user?.smsOptInConsent?.consented !== smsOptInConsent
          ? { smsOptInConsent }
          : {}),
      });

      showToast('Profile updated successfully', 'success');

      if (userTypeParam === 'newUser') {
        window.location.href = '/';
      }
    } catch (error) {
      showToast(`Error uploading profile photo: ${error}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormProvider {...profileForm}>
      <FlexBox
        flexDirection={'column'}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <FlexBox
          flexDirection={'column'}
          alignItems={'center'}
          justifyContent={'center'}
          gap={3}
          maxWidth={'sm'}
        >
          <Typography variant="h2">{copyDict[userTypeParam]}</Typography>
          <Box
            component="form"
            autoComplete="off"
            onSubmit={handleSubmit(handleSave)}
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <UserDetailsInputs />

            <PhoneWithOptInInput />

            <ProfileBioInput />

            <NotificationPreferencesInput />

            <ProfileImageInput />

            <FlexBox alignSelf={'flex-end'}>
              <LoadingButton type="submit" loading={isLoading}>
                Save
              </LoadingButton>
            </FlexBox>
          </Box>
        </FlexBox>
      </FlexBox>
    </FormProvider>
  );
};

export default ProfilePage;
